/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import { sharedStyles } from '../../styles';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { DateFormatHelper } from '../../utils/date-format-helper.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { MultiItemFocusBlurMixin } from '../../utils/multi-item-focus-blur-mixin.js';
import { generateYearData } from '../../utils/year-data-generator.js';

/**
 * Component that allows to select months from a
 * set of years.
 *
 * @prop {Number} months - The number of views to be shown. Example months=2 shows 2 views of month selector starting from the given year.
 * The first view will show given month calendar. The second view shows next month calendar.
 * Any number of views can be generated by giving value to months property.
 * @prop {Number} year - Defines the starting year of the month-selector view .
 * @prop {Object} todaysDate - Today's Date of type Date.
 * @prop {Object} selectedDate - The selected date for this picker.
 * @prop {Boolean} futureDateDisable - Whether future dates should be disabled or not.
 * @prop {Boolean} pastDateDisable - Whether past dates should be disabled or not.
 * @prop {Object} minDate - The minimum date below which all dates will be disabled. `minDate` value should be of type Date.
 * @prop {Object} maxDate - The maximum date above which all dates will be disabled. `maxDate` value should be of type Date.
 * @prop {Boolean} responsive - Defines if this component should show responsive styles or not.
 * @prop {String} locale - The locale value used for formatting the months.
 * @fires year-header-clicked - Dispatched when clicked on the year-heading button. The decade to which the clicked year belongs is passed as part of detail object of event.
 * @fires month-clicked - Dispatched when clicked on a month in a year. The clicked month and the corresponding year to which the month belongs is passed as part of detail object of event.
 */

export class SHPrivateMonthSelector extends MultiItemFocusBlurMixin(LitElement) {
  static get properties() {
    return {
      months: { type: Number },
      year: { type: Number },
      futureDateDisable: { type: Boolean },
      pastDateDisable: { type: Boolean },
      todaysDate: { type: Object },
      selectedDate: { type: Object },
      responsive: { type: Boolean, reflect: true },
      minDate: { type: Object },
      maxDate: { type: Object },
      locale: { type: String },
      _monthViewDatas: { type: Array },
      _yearDecadeDatas: { type: Array },
    };
  }
  static get styles() {
    return [
      super.styles,
      sharedStyles,
      css`
        .months {
          display: flex;
          flex-direction: row;
          grid-gap: 0.5rem;
          background-color: var(--base-4);
          width: fit-content;
          box-sizing: border-box;
          box-shadow: var(--shadow-overlay);
          overflow: hidden;
        }
        .date-panel {
          display: block;
          width: 18rem;
          box-sizing: border-box;
          padding: 1rem 0.5rem 1.75rem 0.5rem;
          display: flex;
          flex-direction: column;
          height: 21rem;
        }
        .header-area {
          height: 1.5rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }
        .month-and-year-display {
          width: 4.5rem;
          border-radius: 1rem;
          font: var(--title-1);
          color: var(--text-primary);
          text-align: center;
          line-height: 2rem;
          box-sizing: border-box;
          cursor: pointer;
          height: 2rem;
        }
        :host(:not(.touch-device)) .month-and-year-display:hover {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        .date-grid {
          display: grid;
          grid-template-rows: repeat(4, 3rem);
          grid-template-columns: repeat(3, 4rem);
          grid-gap: 1rem;
          margin: 1.75rem 1.5rem 0rem 1.5rem;
          box-sizing: border-box;
          width: 14rem;
          align-items: center;
        }
        .month {
          font: var(--body-1);
          color: var(--text-primary);
          text-align: center;
          height: 2rem;
          line-height: 2rem;
          border-radius: 1rem;
          cursor: pointer;
        }
        :host(:not(.touch-device)) .month:hover:not(.selected) {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        .month.selected:not(.disabled) {
          background-color: rgb(var(--ui-3), var(--opacity-1)) !important;
          font: var(--title-1);
          color: var(--text-white);
          line-height: 2rem;
        }
        .month.today:not(.selected):not(.disabled) {
          font: var(--title-1);
          color: var(--text-highlight);
          line-height: 2rem;
        }
        .month.disabled {
          color: var(--text-disabled);
          pointer-events: none;
        }
        .icon-dummy {
          width: 2rem;
          height: 2rem;
        }
        :host([responsive]) .months {
          justify-content: center;
          width: 100%;
          box-shadow: none;
          background-color: transparent;
        }
        :host {
          display: block;
        }
      `,
    ];
  }
  constructor() {
    super();
    this._monthViewDatas = [];
    this._yearDecadeDatas = [];
  }
  render() {
    return html`<div class="months">
      ${this._monthViewDatas.map(
        (monthViewData, yearIndex) =>
          html`<div class="date-panel">
            <div class="header-area">
              ${yearIndex === 0
                ? html`<sh-icon
                    icon="arrow-left-s"
                    button
                    size="m"
                    @click="${() => {
                      --this.year;
                      this.generateMonthViewData();
                    }}"
                  ></sh-icon>`
                : html`<div class="icon-dummy"></div>`}
              <div
                class="month-and-year-display focus-item"
                @click="${() => {
                  dispatchCustomEvent(this, 'year-header-clicked', {
                    belongingDecade:
                      this._yearDecadeDatas[yearIndex].year -
                      (this._yearDecadeDatas[yearIndex].year % 10),
                  });
                }}"
                tabindex="0"
              >
                ${this._yearDecadeDatas[yearIndex].year}
              </div>
              ${yearIndex === this._monthViewDatas.length - 1
                ? html`<sh-icon
                    icon="arrow-right-s"
                    button
                    size="m"
                    @click="${() => {
                      ++this.year;
                      this.generateMonthViewData();
                    }}"
                  ></sh-icon>`
                : html`<div class="icon-dummy"></div>`}
            </div>
            <div class="date-grid">
              <span
                class="${classMap(this._getComputedClasses(monthViewData[0]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 0,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[0].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(0, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[1]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 1,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[1].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(1, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[2]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 2,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[2].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(2, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[3]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 3,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[3].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(3, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[4]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 4,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[4].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(4, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[5]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 5,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[5].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(5, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[6]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 6,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[6].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(6, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[7]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 7,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[7].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(7, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[8]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 8,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[8].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(8, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[9]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 9,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[9].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(9, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[10]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 10,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[10].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(10, this.locale)}
              </span>
              <span
                class="${classMap(this._getComputedClasses(monthViewData[11]))}"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-clicked', {
                    clickedMonth: 11,
                    clickedYear: this._yearDecadeDatas[yearIndex].year,
                  });
                }}"
                tabindex="${monthViewData[11].isDisabled ? '-1' : '0'}"
              >
                ${DateFormatHelper.getShortMonthName(11, this.locale)}
              </span>
            </div>
          </div>`
      )}
    </div>`;
  }
  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    } else {
      this.setAttribute('tabindex', 0);
    }
  }
  generateMonthViewData() {
    let year = this.year;
    let monthViewDatas = [];
    let yearDecadeDatas = [];
    for (let i = 0; i < this.months; i++) {
      monthViewDatas.push(
        generateYearData(
          year,
          this.todaysDate,
          this.selectedDate,
          this.futureDateDisable,
          this.pastDateDisable,
          this.minDate,
          this.maxDate
        )
      );
      yearDecadeDatas.push({ year: year, decade: year - (year % 10) });
      ++year;
    }
    this._monthViewDatas = monthViewDatas;
    this._yearDecadeDatas = yearDecadeDatas;
  }
  /**
   *
   * @param {MonthData} monthObject
   */
  _getComputedClasses(monthObject) {
    return {
      month: true,
      'focus-item': true,
      today: monthObject.isThisMonth,
      selected: monthObject.isSelected,
      disabled: monthObject.isDisabled,
    };
  }
  _enterKeyAction(focusedElement) {
    if (focusedElement !== this) {
      focusedElement.click();
    }
  }
}
customElements.define('sh-private-month-selector', SHPrivateMonthSelector);
